import React from 'react';
import { Checkbox, Tooltip } from '@mui/material';
import { formatDateDDMMYYYY } from '~/helpers/utils.ts';
import { showSorterTooltip, stylePadding } from '../styles.ts';
import { ComplexServiceCheckbox } from '../components/ComplexServiceCheckbox.tsx';
import { RadioGroup } from '../components/RadioGroup.tsx';
import {
  getSafeUniqueOptions,
  getSelectedKeyValue,
  getStatusText,
  getStatusTooltip,
  getUniqueOptions,
  OPTION_ALL,
  REVOKE_STATUS_FILTER_OPTIONS,
  safeStringSorter,
} from '../utils/utils.ts';
import {
  ComplexServiceCheckboxValue,
  FilterDropdownProps,
  TRevokeColumnType,
  TSubmitRecord,
} from '../types.ts';
import { TooltipCheckbox } from '../components/TooltipCheckbox.tsx';
import { DocumentActions } from '../components/DocumentActions.tsx';

interface IColumnProps {
  content: TSubmitRecord[];
  handleDocumentClick: (id: string, documentId: string) => void;
  onCheckboxChange: (recordKey: string | number, fieldName: string, checked: boolean) => void;
}

export const getRevokeColumns = ({
  content = [],
  handleDocumentClick,
  onCheckboxChange,
}: IColumnProps): TRevokeColumnType[] => [
  {
    title: 'РНОКПП',
    dataIndex: 'identificationNumber',
    key: 'identificationNumber',
    sorter: safeStringSorter('identificationNumber'),
    showSorterTooltip,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      <RadioGroup
        value={selectedKeys[0] || OPTION_ALL.value}
        options={[
          OPTION_ALL,
          ...getUniqueOptions(content, (item) => item.identificationNumber || ''),
        ]}
        onChange={(e) => {
          const val = e.target.value;
          setSelectedKeys(val === OPTION_ALL.value ? [] : [val]);
          confirm();
        }}
      />
    ),
    onFilter: (value, record) => value === undefined || record.identificationNumber === value,
  },
  {
    title: 'Дата подачі',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (createdAt: unknown) => formatDateDDMMYYYY(String(createdAt ?? '')),
    sorter: safeStringSorter('createdAt'),
    showSorterTooltip,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: FilterDropdownProps) => {
      const currentValue = getSelectedKeyValue(selectedKeys);

      return (
        <RadioGroup
          value={currentValue}
          options={[
            OPTION_ALL,
            ...getSafeUniqueOptions(content, (item) => formatDateDDMMYYYY(item.createdAt)),
          ]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSelectedKeys(val === OPTION_ALL.value ? [] : [val]);
            confirm();
          }}
        />
      );
    },
    onFilter: (value: string | number, record: TSubmitRecord) =>
      value === undefined || formatDateDDMMYYYY(record.createdAt) === value,
  },
  {
    title: 'Статус заявки',
    dataIndex: 'status',
    key: 'status',
    onCell: () => stylePadding,
    render: (status) => {
      const normalizedStatus = String(status ?? '');
      const tooltipText = getStatusTooltip(normalizedStatus, 'revoke');

      return (
        <Tooltip title={tooltipText}>
          <span>{getStatusText(normalizedStatus, 'revoke')}</span>
        </Tooltip>
      );
    },
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      <RadioGroup
        value={selectedKeys[0] || OPTION_ALL.value}
        options={[OPTION_ALL, ...REVOKE_STATUS_FILTER_OPTIONS]}
        onChange={(e) => {
          const val = e.target.value;
          setSelectedKeys(val === OPTION_ALL.value ? [] : [val]);
          confirm();
        }}
      />
    ),
    onFilter: (value, record) => value === undefined || record.status === value,
  },
  {
    title: <TooltipCheckbox tooltipText="Надання Банком фінансових послуг" text="1" />,
    dataIndex: 'financialServices',
    key: 'financialServices',
    onCell: () => stylePadding,
    render: (val: unknown, record: TSubmitRecord) => (
      <ComplexServiceCheckbox
        value={val as ComplexServiceCheckboxValue | null}
        recordKey={record.key}
        fieldName="financialServices"
        onChange={onCheckboxChange}
      />
    ),
  },
  {
    title: <TooltipCheckbox tooltipText="Отримання карти – перепустки" text="2" />,
    dataIndex: 'cardAccess',
    key: 'cardAccess',
    onCell: () => stylePadding,
    render: (_: unknown, record: TSubmitRecord) => (
      <Checkbox
        checked={!!record.cardAccess?.selected}
        onChange={(e) => {
          if (onCheckboxChange) {
            onCheckboxChange(record.key, 'cardAccess', e.target.checked);
          }
        }}
      />
    ),
  },
  {
    title: <TooltipCheckbox tooltipText="Встановлення ділових відносин" text="3" />,
    dataIndex: 'businessRelationship',
    key: 'businessRelationship',
    onCell: () => stylePadding,
    render: (val: unknown, record: TSubmitRecord) => (
      <ComplexServiceCheckbox
        value={val as ComplexServiceCheckboxValue | null}
        recordKey={record.key}
        fieldName="businessRelationship"
        onChange={onCheckboxChange}
      />
    ),
  },
  {
    title: 'Причина відкликання',
    dataIndex: 'otherReason',
    key: 'otherReason',
  },
  {
    title: 'Дата оновлення',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: (updatedAt) => formatDateDDMMYYYY(String(updatedAt ?? '')),
  },
  {
    title: 'Логін ініціатора',
    dataIndex: 'initiatedByFullName',
    key: 'initiatedByFullName',
    sorter: safeStringSorter('initiatedByFullName'),
    showSorterTooltip,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: FilterDropdownProps) => {
      const currentValue = getSelectedKeyValue(selectedKeys);

      return (
        <RadioGroup
          value={currentValue}
          options={[
            OPTION_ALL,
            ...getSafeUniqueOptions(content, (item) => item.initiatedByFullName ?? ''),
          ]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSelectedKeys(val === OPTION_ALL.value ? [] : [val]);
            confirm();
          }}
        />
      );
    },
    onFilter: (value: string | number, record: TSubmitRecord) =>
      value === undefined || record.initiatedByFullName === value,
  },
  {
    title: 'Документ',
    key: 'actions',
    dataIndex: 'actions',
    width: 120,
    align: 'center' as const,
    render: (_: unknown, record: TSubmitRecord) => (
      <DocumentActions record={record} handleDocumentClick={handleDocumentClick} />
    ),
  },
];
